import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { CheckCircle2, Layers, ShieldCheck } from 'lucide-react';

const ProductShowcase = ({ sections: propSections }) => {
  const { data: fetchedSections, isLoading } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: () => axiosClient.get('/public/cms/product-sections'),
    enabled: !propSections || propSections.length === 0,
  });

  const sections = propSections || fetchedSections || [];

  if (isLoading && sections.length === 0) {
    return (
      <div className="bg-[#0b0f19] py-32 text-center text-slate-500 font-mono animate-pulse">
        Establishing Secure Data Uplink...
      </div>
    );
  }

  if (sections.length === 0) return null;

  return (
    <div className="bg-[#0b0f19] py-20 md:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <span className="inline-flex rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-teal-400">
            AIRS Platform Modules
          </span>

          <h2 className="mt-6 text-3xl md:text-5xl font-black text-white tracking-tight">
            Built for autonomous detection, response, and recovery.
          </h2>

          <p className="mt-5 text-slate-400 text-base md:text-lg leading-relaxed">
            Explore the protection layers that power AIRS across endpoints, infrastructure, and security operations.
          </p>
        </div>

        <div className="space-y-20 md:space-y-24">
          {sections.map((section, index) => {
            const isEven = index % 2 === 0;
            const accent = isEven
              ? {
                  border: 'border-blue-500/25',
                  soft: 'bg-blue-500/10',
                  text: 'text-teal-400',
                  dot: 'bg-teal-400',
                  glow: 'bg-blue-500/10',
                }
              : {
                  border: 'border-emerald-500/25',
                  soft: 'bg-emerald-500/10',
                  text: 'text-emerald-400',
                  dot: 'bg-emerald-400',
                  glow: 'bg-emerald-500/10',
                };

            return (
              <section
                key={section.id}
                className={`relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                  isEven ? '' : 'lg:[&>*:first-child]:order-2'
                }`}
              >
                <div className="relative group">
                  <div className={`absolute -inset-3 rounded-[2rem] ${accent.glow} blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-500`} />

                  <div className={`relative overflow-hidden rounded-[2rem] border ${accent.border} bg-slate-950/70 shadow-2xl`}>
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />

                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.025]">
                      <div className="flex items-center gap-3">
                        <span className={`h-2.5 w-2.5 rounded-full ${accent.dot} animate-pulse`} />
                        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                          Secure Module {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>

                      <span className={`text-[10px] font-black uppercase tracking-[0.18em] ${accent.text}`}>
                        Active
                      </span>
                    </div>

                    <div className="relative aspect-video lg:aspect-[7/4] bg-slate-900">
                      {section.image_url ? (
                        <img
                          src={section.image_url}
                          alt={section.heading}
                          className="w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-950/80 text-slate-600 font-mono text-sm">
                          <Layers className="w-12 h-12 mb-4 text-slate-700" />
                          [DATA_STREAM_EMPTY: UPLOAD_IMAGE]
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className={`absolute -top-10 ${isEven ? '-right-10' : '-left-10'} h-40 w-40 rounded-full ${accent.glow} blur-3xl opacity-60`} />

                  <div className="relative rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 md:p-8 shadow-2xl backdrop-blur-xl">
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`h-12 w-12 rounded-2xl border ${accent.border} ${accent.soft} flex items-center justify-center`}>
                        <ShieldCheck className={`w-6 h-6 ${accent.text}`} />
                      </div>

                      <div>
                        <span className={`inline-flex rounded-full border ${accent.border} ${accent.soft} px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${accent.text}`}>
                          {section.sub_heading || 'Security Protocol'}
                        </span>
                        <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-600">
                          AIRS Product Layer
                        </p>
                      </div>
                    </div>

                    <h2 className="text-3xl md:text-5xl font-black text-white leading-[1.08] tracking-tight">
                      {section.heading}
                    </h2>

                    <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
                      {section.content}
                    </p>

                    {section.bullets && section.bullets.length > 0 && (
                      <ul className="mt-7 space-y-4 pt-6 border-t border-white/10">
                        {section.bullets.map((point, i) => (
                          <li key={i} className="flex items-start text-slate-300 group/item">
                            <CheckCircle2 className={`w-5 h-5 mr-4 mt-0.5 flex-shrink-0 transition-transform group-hover/item:scale-110 ${accent.text}`} />
                            <span className="text-sm md:text-base leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-8 flex items-center gap-3">
                      <div className={`h-1 w-14 rounded-full ${accent.dot}`} />
                      <div className="h-1 w-5 rounded-full bg-slate-700" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Protected Workflow
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;